import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft, Award, Download, Share2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { getModuleBySlug } from "../data/mock";
import { useProgress, progressPercent } from "../context/ProgressContext";
import { author } from "../data/parents";

const Certificate = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { children, activeChild, findChild } = useProgress();
  const m = getModuleBySlug(slug);
  const child = (params.get("child") ? findChild(params.get("child")) : null) || (params.get("child") ? null : activeChild) || children.find((c) => c.id === params.get("child")) || null;
  const certRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const autoDownloaded = useRef(false);

  const fileName = () => `${(child?.panda?.name || "certificate").replace(/[^a-z0-9]+/gi, "-")}-lesson-${m?.num}-certificate.png`;

  const render = async () => {
    const node = certRef.current;
    return toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await render();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName();
      a.click();
      toast.success("Certificate image downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not create the image. Try the Print option instead.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const dataUrl = await render();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName(), { type: "image/png" });
      const text = `${child.panda.name} completed Lesson ${m.num}: ${m.title} on Teach Your Child Credit!`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Certificate of achievement", text });
      } else {
        await navigator.share({ title: "Certificate of achievement", text, url: window.location.href });
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("Sharing isn't available here - use Download instead.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Auto-download when arriving from the gallery with ?download=1 (once fonts are ready)
  useEffect(() => {
    if (params.get("download") !== "1" || autoDownloaded.current || !m || !child) return;
    if (progressPercent(child, m.id) !== 100) return;
    autoDownloaded.current = true;
    const t = setTimeout(() => { document.fonts?.ready?.then(download) || download(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.id, child?.id]);


  if (!m || !child) {
    return (
      <div className="p-20 text-center">
        <p className="text-gray-600">Certificate not found.</p>
        <Link to="/family" className="text-[#1b8bc9] font-bold underline">Back to the family dashboard</Link>
      </div>
    );
  }

  const earned = progressPercent(child, m.id) === 100;
  const date = child.completed?.[m.id]?.certifiedAt ? new Date(child.completed[m.id].certifiedAt) : new Date();
  const dateStr = date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const quiz = child.completed?.[m.id]?.quiz;

  return (
    <div className="bg-[#f7fdfe] min-h-[80vh] py-10 px-4" data-testid="certificate-page">
      <style>{`@media print { @page { size: landscape; margin: 10mm; } body { background: #fff; } }`}</style>
      <div className="max-w-5xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-4"><Link to="/family" className="inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid="cert-back"><ArrowLeft size={18} /> Family dashboard</Link><Link to="/certificates" className="inline-flex items-center gap-1 font-bold text-[#8a6d00] hover:text-[#e5356f]" data-testid="cert-gallery-link"><Award size={16} /> Certificate wall</Link></div>
        {earned ? (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={download} disabled={busy} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="download-certificate"><Download size={18} /> {busy ? "Preparing..." : "Download image"}</button>
            {canShare && (
              <button onClick={share} disabled={busy} className="btn-pill bg-[#63c79a] hover:bg-[#4fb98a] text-white inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="share-certificate"><Share2 size={18} /> Share</button>
            )}
            <button onClick={() => window.print()} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-2 shadow" data-testid="print-certificate"><Printer size={18} /> Print</button>
          </div>
        ) : (
          <Link to={`/lessons/${m.slug}`} className="btn-pill text-white" style={{ background: m.button }}>Finish the lesson to unlock</Link>
        )}
      </div>

      <div ref={certRef} className={`certificate max-w-5xl mx-auto bg-white relative overflow-hidden ${earned ? "" : "opacity-60 grayscale"}`} data-testid="certificate">
        <div className="absolute inset-3 border-[6px] rounded-2xl pointer-events-none" style={{ borderColor: m.color }} />
        <div className="absolute inset-6 border-2 rounded-xl pointer-events-none" style={{ borderColor: m.dark, opacity: 0.35 }} />
        {["top-8 left-8", "top-8 right-8", "bottom-8 left-8", "bottom-8 right-8"].map((pos) => (
          <span key={pos} className={`absolute ${pos} w-6 h-6 rounded-full`} style={{ background: m.color }} />
        ))}

        <div className="relative px-10 sm:px-20 py-14 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="font-serif italic font-bold text-[#1b8bc9] text-lg" style={{ fontFamily: "Literata, serif" }}>Teach your child</span>
            <span className="font-logo text-2xl">
              {["C", "r", "e", "d", "i", "t"].map((l, i) => <span key={i} style={{ color: ["#e5356f", "#f7931e", "#63c79a", "#1b8bc9", "#7d5fb2", "#f3c932"][i] }}>{l}</span>)}
            </span>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.4em] font-bold text-gray-500">Certificate of achievement</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold" style={{ fontFamily: "Literata, serif", color: m.dark }}>This certifies that</h1>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-8">
            <Panda size={150} accessory="crown" scarf={child.panda.scarf} item="coin" />
            <div>
              <p className="font-logo text-6xl sm:text-7xl leading-none" style={{ color: m.button }} data-testid="cert-child-name">{child.panda.name}</p>
              <div className="h-1.5 w-full rounded-full mt-3" style={{ background: m.color }} />
            </div>
          </div>

          <p className="mt-8 text-lg text-gray-700">has completed all six steps of Lesson {m.num}</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>{m.title}</h2>
          <p className="mt-2 text-gray-600">
            Ages {m.ages} &middot; {m.tagline}
            {quiz !== undefined && <> &middot; Quiz score {quiz}/{m.quiz.length}</>}
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 items-end text-sm">
            <div>
              <p className="font-bold text-[#0c4a6e] border-t-2 border-gray-300 pt-2">{dateStr}</p>
              <p className="text-gray-500">Date</p>
            </div>
            <div className="flex justify-center">
              <span className="w-24 h-24 rounded-full flex flex-col items-center justify-center text-white shadow-lg" style={{ background: m.button }}>
                <Award size={30} />
                <span className="text-[10px] font-bold uppercase tracking-wider mt-1">Lesson {m.num}</span>
              </span>
            </div>
            <div>
              <p className="font-bold text-[#0c4a6e] border-t-2 border-gray-300 pt-2" style={{ fontFamily: "Literata, serif", fontStyle: "italic" }}>{author.name}</p>
              <p className="text-gray-500">Author, Teach Your Child Credit</p>
            </div>
          </div>
        </div>
      </div>

      {!earned && (
        <p className="text-center text-gray-500 mt-6 no-print">Complete Learn, Talk, Practice, Story, Do &amp; Don't and the Quiz to earn this certificate.</p>
      )}
    </div>
  );
};

export default Certificate;
